import { ReactNode } from 'react'
import { Badge, Box, Card, Flex, Inset, Text } from '@radix-ui/themes'
import { Car, Images, Instagram } from 'lucide-react'
import { CarCardCar } from '@/types/shared'
import ColorSwatch from './ColorSwatch'

interface Props {
  car: CarCardCar
  /** Rendered under the details — edit/delete in the garage. */
  actions?: ReactNode
}

/**
 * How a car is shown wherever a photographer might meet it. The heading leads
 * with what they remember — the color, then year, make and model — so a list of
 * these can be scanned without opening any of them.
 */
export default function CarCard({ car, actions }: Props) {
  const heading = [car.year, car.make, car.model].filter(Boolean).join(' ')

  return (
    <Card size="2">
      <Inset clip="padding-box" side="top" pb="current">
        {car.cover_thumb_url ? (
          <img
            src={car.cover_thumb_url}
            alt={`${car.driver_name}'s ${heading}`}
            style={{ display: 'block', objectFit: 'cover', width: '100%', height: 220, backgroundColor: 'var(--gray-5)' }}
          />
        ) : (
          <Flex align="center" justify="center" style={{ height: 220, backgroundColor: 'var(--gray-3)' }}>
            <Car size={48} color="var(--gray-8)" />
          </Flex>
        )}
      </Inset>

      {car.review_status === 'pending' && (
        <Badge color="amber" mb="2">Pending review</Badge>
      )}
      {car.review_status === 'rejected' && (
        <Box mb="2">
          <Badge color="red">Not approved</Badge>
          <Text as="p" size="2" color="red" mt="1">
            {car.rejection_reason_label} Edit your car to resubmit it.
          </Text>
        </Box>
      )}

      <Flex align="center" gap="2">
        {car.color_family && <ColorSwatch color={car.color_family} />}
        <Text as="div" size="4" weight="bold">
          {car.color_family && `${car.color_family.label} `}
          {heading}
        </Text>
      </Flex>

      <Flex gap="3" mt="1" align="center" wrap="wrap">
        <Text size="2" color="gray">{car.driver_name}</Text>
        {car.instagram && (
          <Flex align="center" gap="1">
            <Instagram size={14} color="var(--gray-11)" />
            <Text size="2" color="gray">@{car.instagram}</Text>
          </Flex>
        )}
        {car.photo_count > 1 && (
          <Flex align="center" gap="1">
            <Images size={14} color="var(--gray-11)" />
            <Text size="2" color="gray">{car.photo_count}</Text>
          </Flex>
        )}
      </Flex>

      {actions && <Box mt="3">{actions}</Box>}
    </Card>
  )
}
